import { formatNumber, formatUsdCents } from '../lib/format';
import type { VersionCost } from '../features/keys/versions';

/** "version 3", "versions 2 and 3", "versions 1, 2 and 3". */
export const versionsPhrase = (vs: readonly string[]): string =>
  vs.length === 1
    ? `version ${vs[0]}`
    : `versions ${vs.slice(0, -1).join(', ')} and ${vs[vs.length - 1]}`;

/** "1 version", "3 versions". */
const versionsWord = (n: number): string => (n === 1 ? '1 version' : `${formatNumber(n)} versions`);

/**
 * The day extra versions start to be billed, said wherever their cost is: before it
 * nothing is billed for them, and a sentence that names it stays true after it. The API's
 * EXTRA_VERSIONS_FROM, in rypt-core's daily.rs; the site's terms promise thirty days'
 * notice of a price increase, which is why it is not sooner.
 */
export const EXTRA_VERSIONS_FROM = 'December 1, 2026';
const FROM = `From ${EXTRA_VERSIONS_FROM}`;

/** The key detail screen and its three tabs. */
export const keyDetail = {
  /**
   * The browser title's page part. Static on purpose: a key's name must never reach
   * document.title, which analytics sends as the page title.
   */
  documentTitle: 'Key',

  loading: 'Loading key',
  /** Before the first read there is no name, only the id from the address, so it is not quoted. */
  loadFailed: (id: string) => `Key ${id} could not be read.`,
  /** Over a key already on screen, which stays: a failed refetch does not blank it. */
  refreshFailed: (name: string) => `"${name}" could not be refreshed. This is the last read.`,

  tabs: {
    /** Names the strip, not an item in it: "Overview" would be one of its own children. */
    label: 'Key sections',
    overview: 'Overview',
    versions: 'Versions',
    activity: 'Activity',
  },

  overview: {
    id: 'Key id',
    /** The copy control beside the id: its accessible name and its tooltip. */
    copyId: 'Copy key id',
    tier: 'Tier',
    state: 'State',
    created: 'Created',
    deleteRequested: 'Deleted',
    destroyAfter: 'Destroyed after',
    primary: 'Primary version',
    /**
     * Every version, the retiring ones included, against what the tier includes: a paid
     * key pays for each beyond those, retiring or not, until it is destroyed (statement
     * 7.1 of the plan for extra versions). A free key keeps one, and pays for none.
     */
    versionCount: 'Number of versions',
    versionsOf: (c: VersionCost, paid: boolean) => {
      if (!paid) {
        return c.versions === 1
          ? '1, the one version a free key keeps'
          : `${formatNumber(c.versions)}, of which a free key keeps one; the others are retiring`;
      }
      return c.extra > 0
        ? `${formatNumber(c.versions)}, ${formatNumber(c.extra)} more than the ${formatNumber(c.included)} its tier includes`
        : `${formatNumber(c.versions)} of the ${formatNumber(c.included)} its tier includes`;
    },
    /** A paid key's extra versions, and what they cost a month. */
    extraCost: 'Extra versions',
    extraCostOf: (c: VersionCost) =>
      c.extra === 0
        ? `None. ${FROM} each version beyond the ${formatNumber(c.included)} its tier includes costs ${formatUsdCents(c.eachCents)} a month, billed by the day until it is destroyed.`
        : c.extra === 1
          ? `1, at ${formatUsdCents(c.eachCents)} a month, billed by the day from ${EXTRA_VERSIONS_FROM} until it is destroyed.`
          : `${formatNumber(c.extra)}, at ${formatUsdCents(c.eachCents)} a month each, billed by the day from ${EXTRA_VERSIONS_FROM} until each is destroyed: ${formatUsdCents(c.totalCents)} a month in all.`,
    /** A paid key's keep-at-most setting. Free keys have none. */
    retention: 'Automatic retiring',
    retentionOf: (keepAtMost: number | null) =>
      keepAtMost === null
        ? 'Off. Rotations keep every version until you retire it.'
        : `On. Each rotation keeps at most ${versionsWord(keepAtMost)} and retires the oldest beyond that.`,
    /** Shown only while versions are retiring, by a rotation or by hand, and not yet destroyed. */
    retiringCount: 'Retiring versions',
    /** One, by name; several, by count and the first to go. The Versions tab has each. */
    retiring: (versions: readonly string[], firstAfter: string) =>
      versions.length === 1
        ? `Version ${versions[0]}, destroyed after ${firstAfter}`
        : `${versions.length}, the first destroyed after ${firstAfter}`,
    /** A hard limit on free's new ciphertext; on a paid tier the same number is an allowance with overage past it. */
    cap: (paid: boolean) => (paid ? 'Included each month' : 'Monthly cap'),
    /**
     * A paid key's allowance this month, from the usage report: its share of the tier's
     * for its days, to the month's end or to the day its deletion was requested. Shown in
     * place of the tier's figure once the report has it; a key billed all month gets the
     * whole of it, and then the number stands alone.
     */
    includedThisMonth: 'Included this month',
    shareOf: (whole: string) => `its share of ${whole} for the days it is billed this month`,
  },

  versions: {
    heading: 'Versions',
    columns: {
      version: 'Version',
      status: 'Status',
      /** A paid key's only: whether the tier's fee covers the version, or it is extra. */
      billing: 'Billing',
      /** Only while a version is retiring; the others have no such time. */
      destroyAfter: 'Destroyed after',
      /** The row actions' head: hidden, but a screen reader's column has a name. */
      actions: 'Actions',
    },
    /** Paid and free keep versions differently, so each has its own account of them. */
    prose: {
      paid: 'Ciphertext names the version it was made under, so decrypt keeps working across rotations. Rotating this key keeps every old version until you retire it, here or with an API token that has the retire action, unless automatic retiring is turned on, which retires the oldest at each rotation. A retired version still decrypts, unwraps and rewraps until the time beside it, can be restored until then, and is destroyed after that, with anything still encrypted under it. Rewrap moves a ciphertext onto the primary.',
      free: 'Ciphertext names the version it was made under, so decrypt keeps working across rotations. A free key keeps one version, so each rotation retires the one before: a retiring version still decrypts, unwraps and rewraps until the time beside it, and is destroyed after that, with anything still encrypted under it. Rewrap moves a ciphertext onto the primary.',
    },
    /**
     * What a paid key's versions cost, over the table (statement 7.1). The API counts and
     * bills a number of extra versions; the table marks the oldest that many as extra.
     */
    cost: (c: VersionCost) => {
      const has = `This key has ${versionsWord(c.versions)}${c.retiring > 0 ? `, counting ${formatNumber(c.retiring)} retiring` : ''}`;
      if (c.extra === 0) {
        return `${has}, within the ${formatNumber(c.included)} its tier includes, so its versions add nothing to what it costs. ${FROM} each version beyond those costs ${formatUsdCents(c.eachCents)} a month, billed by the day until it is destroyed.`;
      }
      const retiring = c.retiring > 0 ? ' A retiring version counts until it is destroyed.' : '';
      return `${has}, ${formatNumber(c.extra)} more than the ${formatNumber(c.included)} its tier includes. ${FROM} each extra version costs ${formatUsdCents(c.eachCents)} a month, billed by the day until it is destroyed, so the key’s extra versions come to ${formatUsdCents(c.totalCents)} a month.${retiring}`;
    },
    included: 'Included',
    extra: 'Extra',
    empty: 'This key has no enabled versions.',
    rotate: 'Rotate',

    /** Beside every old version that is not retiring. The accessible name says which. */
    retire: 'Retire',
    retireLabel: (version: string) => `Retire version ${version}`,
    /** Beside a paid key's retiring versions, until their time. No confirmation: nothing is lost by it. */
    restore: 'Restore',
    restoreLabel: (version: string) => `Restore version ${version}`,
    /** In place of Restore once a retiring version's time has passed: rypt restores it no more. */
    beingDestroyed: 'Being destroyed',
    restored: (version: string, name: string) =>
      `Restored version ${version} of "${name}". It is no longer retiring, and works as before.`,
    restoreFailed: (version: string) => `Version ${version} was not restored.`,

    /**
     * A paid key's keep-at-most setting, under the table: off by default, when rotations
     * retire nothing. Turning it on, or changing the number, warns that rotations will
     * retire old versions, destroyed seven days later (statement 4.6), and that nothing
     * changes until the next rotation (4.4). A free key has no such setting.
     */
    retention: {
      heading: 'Automatic retiring',
      off: 'Off. Rotating this key keeps every old version until you retire it by hand.',
      on: (kept: number) =>
        `On. Each rotation keeps at most ${versionsWord(kept)} that ${kept === 1 ? 'is' : 'are'} not retiring, the new primary among them, and retires the oldest beyond that. A retired version is destroyed seven days later.`,
      change: 'Change',
      dialogTitle: (name: string) => `Automatic retiring for "${name}"`,
      toggle: 'Retire old versions at each rotation',
      field: 'Versions to keep',
      hint: (included: number) =>
        `Each rotation keeps this many versions that are not retiring, the new primary among them, and retires the oldest beyond them. A number below the ${formatNumber(included)} its tier includes is allowed.`,
      invalid: 'Enter a whole number from 1 up.',
      /** Turning it on, or changing the number: what will be retired, and when. */
      warning: (kept: number) =>
        `With this on, each rotation retires the oldest versions beyond ${formatNumber(kept)}. A retired version keeps working for seven days and is then destroyed, with anything still encrypted under it, unless that is rewrapped first or the version is restored.`,
      takesEffect: 'Nothing is retired now. The setting takes effect at the key’s next rotation.',
      /** What the next rotation would do with the number chosen, from the key as it is now. */
      next: {
        retires: (versions: readonly string[]) =>
          `At its next rotation this key would retire ${versionsPhrase(versions)}.`,
        nothing: (current: number) =>
          `At its next rotation this key would retire nothing: it has ${versionsWord(current)} that ${current === 1 ? 'is' : 'are'} not retiring.`,
        refused: (n: number) =>
          `At its next rotation this key would retire ${formatNumber(n)} versions, more than the ten a key may have retiring at once, so rypt would refuse that rotation. Choose a larger number, or retire old versions by hand first, up to ten at a time.`,
        /** Ten retiring already: a rotation that retires one more waits for one of them to go. */
        full: 'At its next rotation this key would retire one more version while ten are retiring, the most a key may have at once, so rypt would refuse that rotation until one of them is destroyed.',
      },
      /** Turning it off destroys nothing, so it needs no warning, only what it costs. */
      offNote: (c: VersionCost) =>
        `With this off, rotations retire nothing, and every old version is kept until you retire it. ${FROM} each version beyond the ${formatNumber(c.included)} its tier includes costs ${formatUsdCents(c.eachCents)} a month, billed by the day until it is destroyed.`,
      save: 'Save',
      saved: {
        on: (name: string, kept: number) =>
          `Automatic retiring is on for "${name}". From its next rotation, each rotation keeps at most ${versionsWord(kept)}.`,
        off: (name: string) => `Automatic retiring is off for "${name}". Its rotations keep every version.`,
      },
      failed: 'The setting was not changed.',
    },

    /**
     * Retiring one version by hand, which a restore can undo for seven days. The warning
     * says what the user's standing rule asks of anything that can make data unreadable:
     * what happens, when, and how to keep the data (statement 3.8).
     */
    retireDialog: {
      title: (version: string, name: string) => `Retire version ${version} of "${name}"`,
      warning: (version: string, after: string) =>
        `Retiring version ${version} gives it seven days. It keeps decrypting, unwrapping and rewrapping until it is destroyed after ${after}. From then on, anything still encrypted under it can never be decrypted again.`,
      rewrap: (version: string, primary: string) =>
        `Before then, rewrap everything still under version ${version}. Rewrap moves a ciphertext onto the primary, version ${primary}, without the plaintext leaving rypt.`,
      restorable: 'Until it is destroyed you can restore it on this tab, and it works as before.',
      /** For a paid key with extra versions: retiring one takes it off the bill only once it is gone. */
      billed:
        'It still counts as one of the key’s versions until it is destroyed, so it stops adding to what the key costs only then.',
      snippetLabel: 'rewrap',
      copySnippet: 'Copy snippet',
      confirm: 'Retire version',
      toast: (version: string, name: string, after: string) =>
        `Retired version ${version} of "${name}". It is destroyed after ${after}; restore it on the Versions tab before then to keep it.`,
      failed: (version: string) => `Version ${version} was not retired.`,
      /**
       * Refusals said before the click, in ink with Retire disabled, as the rotate dialog
       * says its own: ten retiring already, and a paid key while billing has lapsed.
       */
      tooMany: (next: string) =>
        `This key is already retiring ten versions, the most a key may have at once, so rypt refuses to retire another until one of them is destroyed. The next is destroyed after ${next}; this version can be retired after that.`,
      billing: {
        lead: {
          none: 'rypt refuses to retire a version of a paid key while this tenant has no billing set up.',
          unpaid: 'rypt refuses to retire a version of a paid key while this tenant’s billing has an unpaid invoice.',
          lapsed: 'rypt refuses to retire a version of a paid key while this tenant’s billing has lapsed.',
        },
        why: 'Rewrap, the only way to move data off a version before it is destroyed, is refused then too.',
        fix: {
          none: 'Set billing up on the Billing page, then retire it.',
          unpaid: 'Pay the open invoice in Manage billing, on the Billing page, then retire it.',
          lapsed: 'Set billing up again on the Billing page, then retire it.',
        },
        toBilling: 'Go to Billing',
      },
    },
  },

  /**
   * A key pending deletion: refusing everything, restorable until its seven days are
   * over, and destroyed after them. The time is the API's `destroy_after`; the sentence
   * says "after" it because rypt destroys the key at its first sweep past that time.
   */
  pending: {
    banner: (name: string, after: string) =>
      `"${name}" is deleted and refuses every request. It will be destroyed after ${after}; restore it before then to keep it.`,
    /**
     * Past `destroy_after`, by this browser's clock or by the API's 409: still listed
     * until the sweep reaches it, but the API restores nothing from that time on, so
     * there is no Restore beside this. Delete now stays, and finishes the job at once.
     */
    bannerDue: (name: string) =>
      `"${name}" is deleted and refuses every request. Its date has passed and it is being destroyed; it can no longer be restored.`,
    /**
     * Under the banner's sentence, for a paid key where the API has billing and the
     * deletion was requested from October 2026 on: when its billing stopped, and, while
     * it can still be restored, what a restore bills. A month that has ended is not
     * reopened, which matters for a key deleted late in one.
     */
    billedTo: 'It is billed through the day it was deleted (UTC), and not after.',
    restoreBills:
      'If you restore it, it is billed as if it had never been deleted: the days it was pending deletion are billed, except those in a month that has already ended.',
    restore: 'Restore',
    deleteNow: 'Delete now',
    restored: (name: string) => `Restored "${name}". It works again as before.`,
    restoreFailed: 'The key was not restored.',
  },

  /** A destroyed key still has an audit log, and that log is the only remaining account of it. */
  destroyed: {
    banner: (name: string) => `"${name}" is destroyed. Its audit log is still here; nothing else about it is.`,
    /**
     * A destroyed key whose name this page never read: the id is not a name, so it is not
     * quoted as one. The title takes the Usage page's label for the same row.
     */
    title: (shortId: string) => `Destroyed key · ${shortId}`,
    bannerUnnamed: 'This key is destroyed. Its audit log is still here; nothing else about it is.',
    tabDisabled: 'Not available for a destroyed key',
    /**
     * A 404 to Delete, Delete now or Restore: the key went while its page was open, at
     * the end of its seven days or by Delete now elsewhere. In place of the dialog's "was
     * not deleted", which would be false, while the page reads the key again and turns.
     */
    already: (name: string) => `"${name}" has already been destroyed. The page is reading it again.`,
  },

  rename: 'Rename',
  delete: 'Delete',
} as const;
